import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import {
  AffiliateReferralReport,
  AffiliateReferralUserType,
} from '@/services/dashboard/referralReports/referralReports.types';
import * as React from 'react';
import { getAffiliateColumns } from './Column';

interface AffiliateReferralReportTableProps {
  data: AffiliateReferralReport[];
  isLoading: boolean;
  pagination: Pagination;
}

export function AffiliateReferralReportTable({ data, isLoading, pagination }: AffiliateReferralReportTableProps) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getAffiliateColumns(t), [t]);
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const EXPORT_KEYS_BY_COLUMN: Record<string, readonly string[]> = {
    sn: ['SN'],
    affiliateInfo: [t('common.page.tableColumn.affiliateInformation')],
    referredTo: [t('common.page.tableColumn.referredTo')],
    userType: [t('common.form.label.userType')],
    createdDate: [t('common.page.tableColumn.createdDate')],
    rewardPoints: [t('common.page.tableColumn.rewardBalance')],
    updatedDate: [t('common.page.tableColumn.updatedDate')],
  };
  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => ({
      SN: getSerialNumber(table, index),
      [t('common.page.tableColumn.affiliateInformation')]: [row.affiliateInfo, row.emailAddress]
        .filter(Boolean)
        .join(', '),
      [t('common.page.tableColumn.referredTo')]: row.referredTo,
      [t('common.form.label.userType')]:
        row.userType === AffiliateReferralUserType.Customer
          ? t('common.form.label.customer')
          : t('common.form.label.affiliate'),
      [t('common.page.tableColumn.createdDate')]: formatDate(row.actionDate, 'YYYY/MM/DD HH:mm:ss'),
      [t('common.page.tableColumn.rewardBalance')]: formatAmount(row.rewardPoints ?? 0),
      [t('common.page.tableColumn.updatedDate')]: formatDate(row.actionDate, 'YYYY/MM/DD HH:mm:ss'),
    }));
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, EXPORT_KEYS_BY_COLUMN);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'affiliate-referral-report',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.referralReports.tableTittle.recentAffiliateRecords'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + rowIndex + 1,
      actionKey: 'exportCsv',
      action: handleExport,
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row, index) =>
      [
        row.affiliateId,
        row.referredTo,
        row.actionDate,
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + index,
      ].join('-'),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar defaultDatePreset="today" hideViewOptions buttonText="common.page.table.exportCSV" />
    </DataTable>
  );
}
